export const createAnimator = (avatarRef, animationRef) => {
  const startSuperuserAnimation = () => {
    if (!avatarRef.current) return;

    let time = 0;
    const animate = () => {
      if (!avatarRef.current) {
        cancelAnimationFrame(animationRef.current);
        return;
      }
      time += 0.008;

      const frameCount = Math.floor(time * 30);
      const isRedPhase = Math.floor(frameCount / 20) % 2 === 0;

      const flickerIntensity = 0.6 + Math.sin(time * 15) * 0.3;
      const intensity = isRedPhase ? flickerIntensity : flickerIntensity * 0.4;

      const redColor = isRedPhase ? `rgba(255, 0, 0, ${intensity})` : `rgba(0, 0, 0, 0.05)`;
      const blueColor = !isRedPhase ? `rgba(0, 0, 255, ${intensity})` : `rgba(0, 0, 0, 0.05)`;

      const redSize = 12 + Math.sin(time * 4) * 6;
      const blueSize = 12 + Math.cos(time * 4) * 6;

      avatarRef.current.style.boxShadow = `inset -6px 0 ${redSize}px ${redColor}, inset 6px 0 ${blueSize}px ${blueColor}`;
      avatarRef.current.style.filter = `drop-shadow(0 0 ${4 + Math.sin(time * 3) * 3}px ${isRedPhase ? 'rgba(255, 0, 0, 0.5)' : 'rgba(0, 0, 255, 0.5)'})`;

      // Очищаем background для суперюзера
      avatarRef.current.style.background = '';
      avatarRef.current.style.backgroundImage = '';

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();
  };

  const startDonorAnimation = () => {
    if (!avatarRef.current) return;

    let time = 0;

    const animate = () => {
      if (!avatarRef.current) {
        cancelAnimationFrame(animationRef.current);
        return;
      }
      time += 0.008;

      const breathSpeed = 0.8;
      const goldIntensity = 0.5 + Math.sin(time * breathSpeed) * 0.25;
      const goldSize = 8 + Math.sin(time * breathSpeed * 0.8) * 5;

      const goldR = 255;
      const goldG = 200 + Math.sin(time * 0.8) * 40;
      const goldB = 50 + Math.sin(time * 1.2) * 30;

      // Основная тень
      avatarRef.current.style.boxShadow = `inset 0 0 ${goldSize}px rgba(${goldR}, ${goldG}, ${goldB}, ${goldIntensity})`;

      // Блик по всей карточке (только для донатора)
      const blinkSpeed = 1.5;
      const blinkIntensity = Math.abs(Math.sin(time * blinkSpeed)) * 0.4;
      const blinkPosition = (Math.sin(time * blinkSpeed) + 1) / 2;

      const shineGradient = `linear-gradient(
        115deg,
        transparent 0%,
        transparent ${Math.max(0, blinkPosition * 100 - 30)}%,
        rgba(255, 215, 0, ${blinkIntensity * 0.6}) ${Math.max(0, blinkPosition * 100 - 15)}%,
        rgba(255, 255, 200, ${blinkIntensity * 0.9}) ${blinkPosition * 100}%,
        rgba(255, 215, 0, ${blinkIntensity * 0.6}) ${Math.min(100, blinkPosition * 100 + 15)}%,
        transparent ${Math.min(100, blinkPosition * 100 + 30)}%,
        transparent 100%
      )`;

      avatarRef.current.style.background = shineGradient;
      avatarRef.current.style.filter = `drop-shadow(0 0 ${2 + Math.sin(time * 1.2) * 1.5}px rgba(255, 215, 0, ${0.3 + Math.sin(time * 1.5) * 0.15}))`;

      // Добавляем эффект свечения краев для донатора
      if (blinkIntensity > 0.15) {
        const outlineIntensity = blinkIntensity * 0.5;
        avatarRef.current.style.outline = `2px solid rgba(255, 215, 0, ${outlineIntensity})`;
        avatarRef.current.style.outlineOffset = '2px';
      } else {
        avatarRef.current.style.outline = 'none';
      }

      const currentBlinkPosition = blinkPosition;
      setTimeout(() => {
        if (avatarRef.current && Math.abs((Math.sin(time * blinkSpeed) + 1) / 2 - currentBlinkPosition) < 0.1) {
          avatarRef.current.style.background = '';
        }
      }, 150);

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();
  };

  const startLibrarianAnimation = () => {
    if (!avatarRef.current) return;

    let time = 0;
    let glitchCounter = 0;
    let noiseInterval = 0;

    const generatePixelNoise = () => {
      const pixels = [];
      for (let i = 0; i < 12; i++) {
        const x = Math.random() * 100;
        const y = Math.random() * 100;
        const size = 2 + Math.random() * 3;
        const alpha = 0.2 + Math.random() * 0.5;
        pixels.push(`${x}% ${y}% ${size}px ${size}px rgba(0, 200, ${150 + Math.random() * 105}, ${alpha})`);
      }
      return pixels.join(', ');
    };

    const animate = () => {
      if (!avatarRef.current) {
        cancelAnimationFrame(animationRef.current);
        return;
      }
      time += 0.008;
      glitchCounter++;
      noiseInterval++;

      const baseIntensity = 0.3 + Math.sin(time * 2) * 0.15;
      const baseSize = 6 + Math.sin(time * 2.5) * 2;

      const shouldAddNoise = noiseInterval > 4;
      let boxShadow = '';
      let filter = '';
      let imageRendering = '';

      const shouldGlitch = Math.random() < 0.08;
      const isStrongGlitch = glitchCounter > 45 && Math.random() < 0.25;

      // Очищаем background для библиотекаря (только рамка)
      avatarRef.current.style.background = '';
      avatarRef.current.style.backgroundImage = '';

      if (shouldAddNoise) {
        noiseInterval = 0;
        const pixelNoise = generatePixelNoise();
        // Для библиотекаря добавляем шум только в тень, не заполняя карточку
        boxShadow += `inset 0 0 ${baseSize + 2}px rgba(0, 200, 150, 0.3)`;
        imageRendering = 'pixelated';
      }

      if (isStrongGlitch) {
        glitchCounter = 0;

        const flashIntensity = 0.8 + Math.sin(time * 10) * 0.3;
        const flashColor = `rgba(0, ${150 + Math.sin(time * 8) * 50}, ${150 + Math.sin(time * 6) * 50}, ${flashIntensity})`;

        boxShadow = `
          inset -4px 0 12px rgba(0, 200, 100, 0.7),
          inset 4px 0 12px rgba(0, 150, 200, 0.7),
          inset 0 -4px 12px ${flashColor},
          inset 0 4px 12px rgba(0, 200, 150, 0.6)
        `;

        const redShift = (Math.sin(time * 15) * 3);
        const blueShift = (Math.cos(time * 13) * 3);
        filter = `
          drop-shadow(${redShift}px 0 0 rgba(255, 0, 0, 0.6))
          drop-shadow(${blueShift}px 0 0 rgba(0, 0, 255, 0.6))
        `;

      } else if (shouldGlitch) {
        const glitchIntensity = 0.5 + Math.sin(time * 20) * 0.3;
        const glitchColor = `rgba(0, ${150 + Math.sin(time * 15) * 50}, ${150 + Math.cos(time * 12) * 50}, ${glitchIntensity})`;

        const barPosition = Math.sin(time * 8) * 30 + 50;

        boxShadow = `
          inset 0 0 ${baseSize + 4}px ${glitchColor},
          inset 0 ${barPosition}% 0 0 rgba(0, 200, 100, 0.5)
        `;

        const redShift = Math.sin(time * 18) * 3;
        const blueShift = Math.cos(time * 16) * 3;
        filter = `
          drop-shadow(${redShift}px 0 0 rgba(255, 0, 0, 0.5))
          drop-shadow(${blueShift}px 0 0 rgba(0, 0, 255, 0.5))
        `;

      } else if (shouldAddNoise) {
        const noiseIntensity = 0.2 + Math.sin(time * 8) * 0.15;
        const noiseColor = `rgba(0, ${180 + Math.sin(time * 10) * 50}, ${160 + Math.cos(time * 8) * 50}, ${noiseIntensity})`;

        boxShadow = `inset 0 0 ${baseSize + 2}px ${noiseColor}`;
        filter = `blur(${0.3 + Math.sin(time * 12) * 0.2}px)`;

      } else {
        const greenHue = 120 + Math.sin(time * 1.5) * 40;
        const blueHue = 160 + Math.sin(time * 2) * 60;

        boxShadow = `inset 0 0 ${baseSize}px rgba(0, ${greenHue}, ${blueHue}, ${baseIntensity})`;
        filter = `drop-shadow(0 0 ${2 + Math.sin(time * 3) * 1}px rgba(0, 200, 150, 0.4))`;
      }

      avatarRef.current.style.boxShadow = boxShadow;
      avatarRef.current.style.filter = filter;
      avatarRef.current.style.imageRendering = imageRendering;

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();
  };

  const startModeratorAnimation = () => {
    if (!avatarRef.current) return;

    let time = 0;
    const animate = () => {
      if (!avatarRef.current) {
        cancelAnimationFrame(animationRef.current);
        return;
      }
      time += 0.004;

      const deepSpeed = 0.8;
      const intensity = 0.3 + Math.sin(time * deepSpeed) * 0.2;
      const size = 8 + Math.sin(time * deepSpeed * 0.6) * 4;

      const blueGlow = 120 + Math.sin(time * 1.2) * 40;

      avatarRef.current.style.boxShadow = `inset 0 0 ${size}px rgba(51, ${blueGlow}, 255, ${intensity})`;
      avatarRef.current.style.filter = `drop-shadow(0 0 ${1.5 + Math.sin(time * 1) * 1}px rgba(51, 100, 255, 0.4))`;

      // Очищаем background для модератора
      avatarRef.current.style.background = '';
      avatarRef.current.style.backgroundImage = '';

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();
  };

  const startLegendAnimation = () => {
    if (!avatarRef.current) return;

    let time = 0;
    const animate = () => {
      if (!avatarRef.current) {
        cancelAnimationFrame(animationRef.current);
        return;
      }
      time += 0.01;

      const epicSpeed = 1.5;
      const intensity = 0.6 + Math.sin(time * epicSpeed) * 0.25;
      const size = 12 + Math.sin(time * epicSpeed * 0.8) * 8;

      const angle = time * 1.2;
      const offsetX = Math.sin(angle) * 2;
      const offsetY = Math.cos(angle) * 2;

      const goldIntensity = 0.4 + Math.sin(time * 2) * 0.2;

      avatarRef.current.style.boxShadow = `inset 0 0 ${size}px rgba(255, 204, 68, ${intensity})`;
      avatarRef.current.style.filter = `drop-shadow(${offsetX}px ${offsetY}px ${3 + Math.sin(time * 3) * 2}px rgba(255, 204, 68, ${goldIntensity}))`;

      // Легенда также может иметь свой фон (оставляем как есть или добавляем)
      // avatarRef.current.style.background = ''; // Раскомментировать если не нужен фон

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();
  };

  const startDefaultAnimation = () => {
    if (!avatarRef.current) return;

    avatarRef.current.style.boxShadow = '';
    avatarRef.current.style.filter = '';
    avatarRef.current.style.background = '';
    avatarRef.current.style.backgroundImage = '';
    avatarRef.current.style.imageRendering = '';
    avatarRef.current.style.outline = '';
  };

  const animators = {
    superuser: startSuperuserAnimation,
    donor: startDonorAnimation,
    librarian: startLibrarianAnimation,
    moderator: startModeratorAnimation,
    legend: startLegendAnimation,
    default: startDefaultAnimation
  };

  return (type, stopOnly = false) => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }

    if (stopOnly) return;

    const animator = animators[type];
    if (animator) {
      animator();
    } else {
      startDefaultAnimation();
    }
  };
};